import SlimSelect from 'slim-select'
import {tns} from 'tiny-slider'
import tippy from 'tippy.js'
import pasteModalInDom from "./helpers/pasteModalInDom";
import debounce from "./helpers/custom-debounce";
import suggest from "./dadata-functions/suggest";
import sendOnChange from "./helpers/sendOnChange";
import initAddressSearch from "./dadata-functions/initAddressSearch";
import reg_keyboard from "./helpers/reg_keyboard";
import initPersonalDataButtonsForOpen from "./helpers/initPersonalDataButtonsForOpen";
import initCheckNote from "./helpers/initCheckNote";
import forEach from "./helpers/customForEach";
import initJsModalSubmitReview from "./helpers/initJsModalSubmitReview";
import postData from "./helpers/postData";
import initJsModalSubmitOrder from "./helpers/initJsModalSubmitOrder";
import initAuthorizationModals from "./helpers/initAuthorizationModals";
import initClientSideRenderingModals from "./helpers/initClientSideRenderingModals";
import bodyFixPosition from "./helpers/bodyFixPosition";
import bodyUnfixPosition from "./helpers/bodyUnfixPosition";
import initModal from "./helpers/initModal";
import initSubmitForms from "./helpers/initSubmitForms";
import setRussianErrorMessage from "./helpers/setRussianErrorMessage";
import initRatingDescriptionShowMore from "./helpers/initRatingDescriptionShowMore";
import initCookieBar from "./helpers/initCookieBar";
import initPhoneMaskForModal from "./helpers/phoneMask";
import initHeaderTariffDescriptionShowMore from "./helpers/initHeaderTariffDescriptionShowMore";
import initHeaderProviderDescriptionShowMore from "./helpers/initHeaderProviderDescriptionShowMore";

class tabbisClass {
    init(options) {
        this.thisOptions(options);
        this.thisMemory();
        this.setup();
    }

    // Setup
    setup() {
        const panes = document.querySelectorAll(this.options.paneGroup);
        const tabs = document.querySelectorAll(this.options.tabGroup);

        tabs.forEach((tabGroups, groupIndex) => {
            const paneGroups = panes[groupIndex];
            const activeIndex = this.getActiveIndex(tabGroups, groupIndex);

            tabGroups.setAttribute('role', 'tablist');

            // Reset items
            this.resetTabs([...tabGroups.children]);
            this.resetPanes([...paneGroups.children]);

            [...tabGroups.children].forEach((tabItem, tabIndex) => {
                const paneItem = paneGroups.children[tabIndex];

                // Add attributes
                this.addTabAttributes(tabItem, groupIndex);
                this.addPaneAttributes(tabItem, paneItem);

                tabItem.groupIndex = groupIndex;

                // Trigger event
                tabItem.addEventListener(this.options.trigger, (e) => {
                    this.activate(e.currentTarget, tabItem.groupIndex);
                });

                // Key event
                if (this.options.keyboardNavigation) {
                    tabItem.addEventListener('keydown', (e) => {
                        this.eventKey(e);
                    });
                }
            });

            if (activeIndex !== null) {
                this.activateTab([...tabGroups.children][activeIndex]);
                this.activatePane([...paneGroups.children][activeIndex]);
            }
        });
    }

    // Event key
    eventKey(e) {
        if ([13, 37, 38, 39, 40].includes(e.keyCode)) {
            e.preventDefault();
        }

        if (e.keyCode == 13) {
            e.currentTarget.click();
        } else if ([39, 40].includes(e.keyCode)) {
            this.step(e, 1);
        } else if ([37, 38].includes(e.keyCode)) {
            this.step(e, -1);
        }
    }

    // Index
    index(el) {
        return [...el.parentElement.children].indexOf(el);
    }

    // Step
    step(e, direction) {
        const children = e.currentTarget.parentElement.children;
        this.resetTabindex(children);

        let el = children[this.pos(e.currentTarget, children, direction)];
        el.focus();
        el.setAttribute('tabindex', 0);
    }

    resetTabindex(children) {
        [...children].forEach((child) => {
            child.setAttribute('tabindex', '-1');
        });
    }

    // Pos
    pos(tab, children, direction) {
        let pos = this.index(tab);
        pos += direction;

        if (children.length <= pos) {
            pos = 0;
        } else if (pos == -1) {
            pos = children.length - 1;
        }

        return pos;
    }

    // Emit event
    emitEvent(tab, pane) {
        let event = new CustomEvent('tabbis', {
            bubbles: true,
            detail: {
                tab: tab,
                pane: pane
            }
        });

        tab.dispatchEvent(event);
    }

    // Set active
    getActiveIndex(groupTabs, groupIndex) {
        const memory = this.loadMemory(groupIndex);

        if (typeof memory !== 'undefined') {
            return memory;
        } else {
            let element = groupTabs.querySelector(this.options.tabActive);

            if (!element) {
                element = groupTabs.querySelector('[aria-selected="true"]');
            }

            if (element) {
                return this.index(element);
            } else if (this.options.tabActiveFallback !== false) {
                return this.options.tabActiveFallback;
            } else {
                return null;
            }
        }
    }

    // ATTRIBUTES

    // Add tab attributes
    addTabAttributes(tab, groupIndex) {
        const tabIndex = this.index(tab);
        const prefix = this.options.prefix;

        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', `${prefix}tabpanel-${groupIndex}-${tabIndex}`);
        tab.setAttribute('id', `${prefix}tab-${groupIndex}-${tabIndex}`);
    }

    // Add tabpanel attributes
    addPaneAttributes(tab, pane) {
        pane.setAttribute('role', 'tabpanel');
        pane.setAttribute('aria-labelledby', tab.getAttribute('id'));
        pane.setAttribute('id', tab.getAttribute('aria-controls'));
        pane.setAttribute('tabindex', '0');
    }

    // Activate
    activate(tab, i) {
        const pane = document.querySelector(`#${tab.getAttribute('aria-controls')}`);

        this.resetTabs([...tab.parentNode.children]);
        this.resetPanes([...pane.parentElement.children]);

        this.activateTab(tab);
        this.activatePane(pane);

        this.saveMemory(tab, i);

        this.emitEvent(tab, pane);
    }

    // Activate tab
    activateTab(tab) {
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
    }

    // Activate pane
    activatePane(pane) {
        pane.removeAttribute('hidden');
    }

    // Remove tab attributes
    resetTabs(tabs) {
        tabs.forEach((el) => el.setAttribute('aria-selected', 'false'));
        this.resetTabindex(tabs);
    }

    // Reset pane attributes
    resetPanes(panes) {
        panes.forEach((el) => el.setAttribute('hidden', ''));
    }

    // MEMORY

    // Load memory
    loadMemory(groupIndex) {
        if (!this.options.memory) return;
        if (typeof this.memory[groupIndex] === 'undefined') return;
        if (this.memory[groupIndex] === null) return;

        return parseInt(this.memory[groupIndex]);
    }

    // Save memory
    saveMemory(tab, groupIndex) {
        if (!this.options.memory) return;
        this.memory[groupIndex] = this.index(tab);
        localStorage.setItem(this.options.memory, JSON.stringify(this.memory));
    }

    // This memory
    thisMemory() {
        if (!this.options.memory) return;
        const store = localStorage.getItem(this.options.memory);
        this.memory = store !== null ? JSON.parse(store) : [];
    }

    // OPTIONS

    // Defaults
    defaults() {
        return {
            keyboardNavigation: true,
            memory: false,
            paneGroup: '[data-panes]',
            prefix: '',
            tabActive: '[data-active]',
            tabActiveFallback: 0,
            tabGroup: '[data-tabs]',
            trigger: 'click'
        };
    }

    // This options
    thisOptions(options) {
        this.options = Object.assign(this.defaults(), options);
        if (this.options.memory !== true) return;
        this.options.memory = 'tabbis';
    }
}

// Function call
function tabbis(options = {}) {
    const tabs = new tabbisClass();
    tabs.init(options);
}


document.addEventListener("DOMContentLoaded", function () {
    initCookieBar();
    let mobile = document.body.offsetWidth < 1200;

    let JSFilter = (sort) => {
        let nodeList = document.querySelectorAll(
            ".tarif-grid-item .slider-item"
        );
        let parentNode = nodeList[0].parentNode;
        let itemsArray = [];
        for (let i = 0; i < nodeList.length; i++) {
            itemsArray.push(parentNode.removeChild(nodeList[i]));
        }
        itemsArray
            .sort(function (nodeA, nodeB) {
                let val1 = parseInt(nodeA.dataset[sort]);
                let val2 = parseInt(nodeB.dataset[sort]);
                if (sort == "rating") {
                    if (val1 < val2) return 1;
                    if (val1 > val2) return -1;
                } else {
                    if (val1 < val2) return -1;
                    if (val1 > val2) return 1;
                }
                return 0;
            })
            .forEach(function (node) {
                parentNode.appendChild(node);
            });
    };

    let phoneInput = document.querySelectorAll(".phone-mask");
    forEach(phoneInput, function (index, value) {
        IMask(value, {
            mask: "+{7} (000) 000-00-00",
            prepare: (appended, masked) => {
                if (appended === "8" && masked.value === "") {
                    return "";
                }
                return appended;
            },
        });
    });

    // global func

    function initToggle() {
        let toggle_table = document.querySelectorAll(".providers-content");
        if (toggle_table) {
            [].forEach.call(toggle_table, function (item) {
                item.querySelector(".providers-toggle__nav").addEventListener(
                    "click",
                    function () {
                        item.querySelector(
                            ".providers-toggle__nav"
                        ).classList.toggle("is-active");
                        item.querySelector(
                            ".providers-toggle__content"
                        ).classList.toggle("is-active");
                    }
                );
            });
        }
    }

    function initRating() {
        let raiting = document.querySelectorAll(".js_raiting");
        forEach(raiting, function (index, value) {
            let num = value.dataset.num;
            let size = value.dataset.size;
            let size_p = "";
            size == "big-size" ? (size_p = 19.7) : (size_p = 20.5);
            if (num) {
                let rating = num * size_p;
                value.querySelector(".stars_over_bg").style.width =
                    rating + "%";
            }
        });
    }
    initRatingDescriptionShowMore();
    initHeaderTariffDescriptionShowMore();
    initHeaderProviderDescriptionShowMore();
    function initTippyJS() {
        let tippyJS = document.querySelector("[data-tippy-content]");
        if (tippyJS) {
            tippy("[data-tippy-content]", {
                allowHTML: true,
                theme: "gigaboom",
            });
        }
    }


    initPersonalDataButtonsForOpen();

    function initTariffModal() {
        let modalopen = document.querySelector("[data-custom-open]");
        if (modalopen) {
            let modal_link = document.querySelectorAll("[data-custom-open]");

            modal_link.forEach(function (value) {
                value.addEventListener("click", function (evt) {
                    evt.preventDefault();
                    let type_modal = value.getAttribute("data-custom-open");
                    if (type_modal == "modal-tariff") {
                        let modal = document.getElementById("modal-tariff");
                        if (modal) {
                            modal
                                .querySelector(".popap-accordion_title")
                                .classList.remove("active");
                            modal
                                .querySelector(".popap-accordion_cnt")
                                .classList.remove("popap-accordion_cnt_open");
                            let card = this.closest(".js-modal-tariff");
                            if (card) {
                                const img = document.createElement("img");
                                const cardIcon = modal.querySelector(".icon");
                                let cardIconImage = cardIcon.querySelector("img");
                                img.src = card.dataset.logo;

                                if (!cardIconImage) {
                                    cardIcon.appendChild(img);
                                }

                                if (cardIconImage
                                    && card.dataset.logo !== cardIconImage.getAttribute('src')) {
                                    cardIcon.innerHTML = '';
                                    cardIcon.appendChild(img);
                                }

                                modal.querySelector(
                                    "[name=provider_name]"
                                ).value = card.dataset.provider;
                                modal.querySelector("[name=type]").value =
                                    card.dataset.tariffType;
                                modal.querySelector(
                                    ".popap-accordion_title .name span"
                                ).innerHTML = card.dataset.title;

                                if (card.dataset.speedlimit) {
                                    modal.querySelector(
                                        ".field_speedlimit"
                                    ).style.display = "block";
                                    modal.querySelector(
                                        ".field_speedlimit .name span"
                                    ).innerHTML = card.dataset.speedlimit;
                                } else {
                                    modal.querySelector(
                                        ".field_speedlimit"
                                    ).style.display = "none";
                                    modal.querySelector(
                                        ".field_speedlimit .name span"
                                    ).innerHTML = "";
                                }

                                if (card.dataset.tvchanel) {
                                    modal.querySelector(
                                        ".field_tvlimit"
                                    ).style.display = "block";
                                    modal.querySelector(
                                        ".field_tvlimit .name span"
                                    ).innerHTML = card.dataset.tvchanel;
                                } else {
                                    modal.querySelector(
                                        ".field_tvlimit"
                                    ).style.display = "none";
                                    modal.querySelector(
                                        ".field_tvlimit .name span"
                                    ).innerHTML = "";
                                }

                                if (card.dataset.mob_traffic) {
                                    modal
                                        .querySelector(".field_phonelimit")
                                        .classList.add("hide-mobile");
                                    modal.querySelector(
                                        ".field_phonelimit"
                                    ).style.display = "block";
                                    modal.querySelector(
                                        ".field_phonelimit .name span"
                                    ).innerHTML = card.dataset.mob_traffic;
                                    modal.querySelector(
                                        ".field_phonelimit .desc strong"
                                    ).innerHTML = card.dataset.mob_min;
                                } else {
                                    modal
                                        .querySelector(".field_phonelimit")
                                        .classList.remove("hide-mobile");
                                    modal.querySelector(
                                        ".field_phonelimit"
                                    ).style.display = "none";
                                    modal.querySelector(
                                        ".field_phonelimit .name span"
                                    ).innerHTML = "";
                                    modal.querySelector(
                                        ".field_phonelimit .desc strong"
                                    ).innerHTML = "";
                                }

                                // modal.querySelector('.slider-cards__tooltip').innerHTML = card.querySelector('.slider-cards__tooltip').innerHTML;
                                modal.querySelector(
                                    ".field_connection_fee"
                                ).innerHTML = card.dataset.connection_fee;
                                modal.querySelector(".field_price").innerHTML =
                                    card.dataset.price;
                            }
                        }
                    }
                });
            });
        }
    }


    // tariffs table
    let tabjs = document.querySelector("[data-tabs]");
    if (tabjs) {
        tabbis({
            memory: false,
        });

        let tabjsText = document.querySelector(".tab-js-text");
        if (tabjsText) {
            document.addEventListener(
                "tabbis",
                (e) => {
                    tabjsText.innerHTML = e.detail.tab.dataset.text;
                },
                false
            );
        }
    }

    // page address search tariff

    let home_tariffs = document.querySelectorAll(".search-tariffs-js");
    if (home_tariffs) {
        forEach(home_tariffs, function (index, value) {
            let tariffs_name = value.querySelector(
                "input[name=provider]"
            ).value;
            hometariffsLoad(tariffs_name, value);
        });
    }

    function hometariffsLoad(name, value, limit, from) {
        limit = limit ? limit : 4;
        from = from ? from : 0;
        let containers = value.querySelector(".tariffs_list");
        let url = "";
        if (!isSubdomain) {
            url = "/" + citySlug;
        }
        url +=
            "/tariffs-list?provider_name=" +
            name +
            "&limit=" +
            limit +
            "&from=" +
            from;

        if (value.querySelector("input[name=search]")) {
            url += "&" + value.querySelector("input[name=search]").value;
        }

        let loading = value.querySelector(".loading-img");
        if (loading != undefined) loading.style.display = "none";
        fetch(url)
            .then(function (response) {
                return response.json();
            })
            .then(function (response) {
                if (response.tariffs && response.success == true) {
                    containers.innerHTML = response.tariffs;
                    let load_more = value.querySelector(".load-more");
                    if (load_more) {
                        if (response.has_more)
                            load_more.style.display = "block";
                        else load_more.style.display = "none";

                        load_more
                            .querySelector("button")
                            .addEventListener("click", function (e) {
                                e.preventDefault();
                                hometariffsLoad(name, value, limit + 6);
                            });
                    }

                    initTippyJS();
                    initModal();
                    initTariffModal();
                } else {
                    containers.innerHTML =
                        '<div class="page-content__alert none">Тарифы не найдены.</div>';
                }
            });
    }

    function declOfNum(number, titles) {
        let cases = [2, 0, 1, 1, 1, 2];
        return titles[
            number % 100 > 4 && number % 100 < 20
                ? 2
                : cases[number % 10 < 5 ? number % 10 : 5]
            ];
    }

    function updateURL(new_url) {
        if (history.pushState) {
            var baseUrl =
                window.location.protocol +
                "//" +
                window.location.host +
                window.location.pathname;
            var newUrl = baseUrl + new_url;
            history.pushState(null, null, newUrl);
        } else {
            console.warn("History API не поддерживается");
        }
    }

    function getGet(name) {
        var s = window.location.search;
        s = s.match(new RegExp(name + "=([^&=]+)"));
        return s ? s[1] : false;
    }

    function parseNumber(value) {
        return parseFloat(value.replace(',', '.'));
    }

    initRating();
    initModal();
    initTariffModal();
    initTippyJS();
    initToggle();


    forEach(
        document.querySelectorAll("[data-service-cost]"),
        function (index, checkbox) {
            checkbox.addEventListener("click", function () {
                if (checkbox.checked) {
                    let toggle = document.getElementById(
                        checkbox.dataset.toggle
                    );
                    if (toggle) {
                        toggle.checked = false;
                    }
                }
            });

            checkbox.addEventListener("change", function () {
                let sum = 0,
                    iSum = 0;

                forEach(
                    document.querySelectorAll("[data-service-cost]:checked"),
                    (index, el) => {
                        if (el.dataset.type != "installment") {
                            sum += parseNumber(el.dataset.serviceCost); // Используем parseFloat
                        } else {
                            iSum += parseNumber(el.dataset.serviceCost); // Используем parseFloat
                        }
                    }
                );

                forEach(
                    document.querySelectorAll("[data-price]"),
                    (index, el) => {
                        el.innerHTML = parseFloat(el.dataset.price) + sum; // Используем parseFloat
                    }
                );

                forEach(
                    document.querySelectorAll("[data-i-price]"),
                    (index, el) => {
                        el.innerHTML = parseFloat(el.dataset.iPrice) + iSum; // Используем parseFloat
                    }
                );
            });
        }
    );

    initClientSideRenderingModals();

    // menu

    let open_menu = document.querySelectorAll(".header__burger");
    let close_menu = document.querySelector(".fixed_menu_close");
    let fixed_menu = document.querySelector(".fixed_menu");

    forEach(open_menu, function (index, value) {
        value.onclick = function (evt) {
            evt.preventDefault();
            bodyFixPosition();
            fixed_menu.classList.add("nav-open");
        };
    });

    if (close_menu) {
        close_menu.onclick = function (evt) {
            evt.preventDefault();
            bodyUnfixPosition();
            fixed_menu.classList.remove("nav-open");
        };
    }

    // dropdown menu
    let header__drop_open = document.querySelectorAll(".header__drop-title");
    forEach(header__drop_open, function (index, value) {
        value.onclick = function () {
            let isClosed = value
                .closest(".header__drop")
                .classList.contains("is-active");
            let drop_active = value
                .closest(".header")
                .querySelectorAll(".header__drop");
            Array.from(drop_active).forEach((item) => {
                item.classList.remove("is-active");
            });
            // если меню было закрыто, то открываем его
            if (isClosed == false) {
                value.closest(".header__drop").classList.add("is-active");
            }
        };
    });

    document.addEventListener("click", function (event) {
        var e = document.querySelector(".header__drop.is-active");
        if (e) {
            if (!e.contains(event.target)) e.classList.remove("is-active");
        }
    });

    // dropdown rating
    let ratings__drop_open = document.querySelectorAll(".ratings__drop-title");
    forEach(ratings__drop_open, function (index, value) {
        value.onclick = function () {
            let isClosed = value
                .closest(".ratings__drop")
                .classList.contains("is-active");
            let drop_active = value
                .closest(".ratings-table")
                .querySelectorAll(".ratings__drop");
            Array.from(drop_active).forEach((item) => {
                item.classList.remove("is-active");
            });
            // если меню было закрыто, то открываем его
            if (isClosed == false) {
                value.closest(".ratings__drop").classList.add("is-active");
            }
        };
    });

    document.addEventListener("click", function (event) {
        var e = document.querySelector(".ratings__drop.is-active");
        if (e) {
            if (!e.contains(event.target)) e.classList.remove("is-active");
        }
    });

    // spoiler
    let js_accordion = document.querySelectorAll(".js-accordion-open");
    forEach(js_accordion, function (index, value) {
        value.onclick = function () {
            value
                .closest(".js-accordion")
                .querySelector(".js-accordion-content")
                .classList.toggle("is-active");
            value.classList.toggle("active");
        };
    });

    let popap_accordion = document.querySelectorAll(".popap-accordion_title");
    forEach(popap_accordion, function (index, value) {
        value.onclick = function () {
            value
                .closest(".popap-accordion")
                .querySelector(".popap-accordion_cnt")
                .classList.toggle("popap-accordion_cnt_open");
            value.classList.toggle("active");
        };
    });

    // modal tab
    let modal_tab = document.querySelectorAll(".modal-tabnav_item");
    forEach(modal_tab, function (index, value) {
        value.onclick = function (evt) {
            evt.preventDefault();
            document
                .querySelector(".modal-tabnav_item.active")
                .classList.remove("active");
            value.classList.add("active");
            document
                .querySelector(".modal-tabcontent_item.active")
                .classList.remove("active");
            document
                .querySelector("[id=" + value.dataset["opentab"] + "]")
                .classList.add("active");
        };
    });

    let fixed_price = document.querySelector("#fixed_price");
    if (fixed_price) {
        let checkPrice = () => {
            // Detect scroll position
            let scrollPosition = Math.round(window.scrollY),
                top_price = document
                    .querySelector("#tarif_block_price")
                    .getBoundingClientRect().top;
            if (scrollPosition - 300 >= top_price) {
                fixed_price.classList.add("hide");
            } else {
                fixed_price.classList.remove("hide");
            }
        };
        window.addEventListener("scroll", checkPrice);
    }

    // open tarif price
    let open_price_tarif = document.querySelectorAll(".open_price_tarif");
    forEach(open_price_tarif, function (index, value) {
        value.onclick = function (e) {
            e.preventDefault();
            document.querySelector("#order_block_price").scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        };
    });

    // scroll to block
    document.querySelectorAll(".scroll_link").forEach((link) => {
        if (link.getAttribute("href") && !link.getAttribute("scrollTo")) {
            link.setAttribute(
                "scrollTo",
                link.getAttribute("href").substring(1)
            );
        }
        // link.addEventListener("click", function (e) {
        //   e.preventDefault();
        //   if (this.getAttribute("href")) {
        //     let href = this.getAttribute("href").substring(1);
        //     const scrollTarget = document.getElementById(href);
        //     const topOffset = 70; // если не нужен отступ сверху
        //     const elementPosition = scrollTarget.getBoundingClientRect().top;
        //     const offsetPosition = elementPosition - topOffset;
        //     window.scrollBy({
        //       top: offsetPosition,
        //       behavior: "smooth",
        //     });
        //   }
        // });
    });

    let isTablet =
        /iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(
            navigator.userAgent.toLowerCase()
        ) || window.innerWidth < 1200;

    let sliders = document.querySelectorAll(".slider-desktop");
    if (sliders) {
        forEach(sliders, function (index, value) {
            let id_slider = value.dataset.id;
            let slider_width = value.dataset.width == "small" ? 260 : 330;
            let slider = tns({
                container: value,
                items: 3,
                controls: true,
                nav: false,
                loop: false,
                gutter: 15,
                mouseDrag: true,
                fixedWidth: slider_width,
                controlsPosition: "bottom",
                slideBy: "page",
                preventActionWhenRunning: false,
                speed: 600,
                disable: true,
                touch: true,
                controlsText: [
                    '<span class="icon-arrow"></span>',
                    '<span class="icon-arrow"></span>',
                ],
            });

            if (isTablet) {
                slider.destroy();
                initModal();
                initTariffModal();
                initTippyJS();
            }
        });

        if (!isTablet) {
            let easeInOutQuad = function (t, b, c, d) {
                t /= d / 2;
                if (t < 1) return (c / 2) * t * t + b;
                t--;
                return (-c / 2) * (t * (t - 2) - 1) + b;
            };

            function scrollTo(element, to, duration) {
                let start = element.scrollLeft,
                    change = to - start,
                    currentTime = 0,
                    increment = 5;

                let animateScroll = function () {
                    currentTime += increment;
                    let val = easeInOutQuad(
                        currentTime,
                        start,
                        change,
                        duration
                    );
                    element.scrollLeft = val;
                    if (currentTime < duration) {
                        setTimeout(animateScroll, increment);
                    }
                };

                animateScroll();
            }

            forEach(
                document.querySelectorAll(".tns-controls"),
                function (index, ctrl) {
                    ctrl.style.display = "flex";

                    let parCont = ctrl.parentNode.parentNode;

                    let cont =
                        ctrl.previousSibling.querySelector(".slider-desktop");

                    let btns = ctrl.querySelectorAll("button");

                    let itemWidth = parCont.classList.contains(
                        "slider-container-big"
                    )
                        ? 340
                        : 273;

                    let itemMul = parCont.classList.contains(
                        "slider-container-big"
                    )
                        ? 3
                        : 4;

                    let itemCount =
                        cont.querySelectorAll(".slider-item").length;
                    let containerPadding = 15;
                    let containerWidth = cont.clientWidth;
                    let maxScrollLeft = (itemCount * (itemWidth)) - containerPadding - containerWidth;

                    if (cont.scrollLeft <= maxScrollLeft) {
                        cont.classList.remove("justify-center");
                    }
                    let checkBtns = function () {
                        btns[0].style.visibility =
                            cont.scrollLeft == 0 ? "hidden" : "visible";
                        btns[1].style.visibility =
                            cont.scrollLeft >= maxScrollLeft
                                ? "hidden"
                                : "visible";
                    };

                    cont.onscroll = checkBtns;

                    let setScroll = function (left) {
                        left = parseInt(left / itemWidth) * itemWidth;
                        //cont.scrollLeft = left;
                        scrollTo(cont, left, 350);
                        checkBtns();
                    };

                    let next = function () {
                        setScroll(cont.scrollLeft + itemWidth * itemMul);
                    };

                    let prev = function () {
                        setScroll(cont.scrollLeft - itemWidth * itemMul);
                    };

                    btns[0].style.visibility = "hidden";

                    btns[0].onclick = function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                        prev();
                        return false;
                    };

                    if (itemCount <= itemMul) {
                        btns[1].style.visibility = "hidden";
                    }

                    btns[1].onclick = function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                        next();
                        return false;
                    };
                }
            );
        }
    }

    let sliders_promo = document.querySelector(".slider_promo");
    if (sliders_promo) {
        sliders_promo = tns({
            preventScrollOnTouch: "auto",
            container: ".slider_promo",
            slideBy: "page",
            items: 1,
            controls: false,
            mouseDrag: false,
            navPosition: "bottom",
        });
    }

    // filter mobile
    let ProviderSort = document.getElementById("js-provider-sort");
    if (ProviderSort) {
        ProviderSort.addEventListener("change", function () {
            let sort = this.value;
            JSFilter(sort);
        });
    }

    // filter desktop ver
    let ProviderSortPC = document.querySelectorAll("#js-provider-sort-pc li");
    forEach(ProviderSortPC, function (index, value) {
        value.onclick = function (e) {
            let act_link = value
                .closest("#js-provider-sort-pc")
                .querySelector("li.is-active");
            if (act_link) {
                act_link.classList.remove("is-active");
            }
            value.classList.add("is-active");
            let sort = value.getAttribute("value");
            JSFilter(sort);
        };
    });

    initJsModalSubmitOrder();

    // ??? mb checkbox with page single tariff

    let tits = document.querySelectorAll(".tarif-info-three");
    [].forEach.call(tits, function (tit) {
        let labels = tit.querySelectorAll("label");
        if (labels.length < 3) return;
        labels[1].style.position = "relative";
        labels[1].style.top = "5px";
    });

    // custom select

    let custom_select = document.querySelectorAll(".js-select");
    let slimSelectInstances = [];

    if (custom_select) {
        [].forEach.call(custom_select, function (item) {
            const instance = new SlimSelect({
                select: item,
                showSearch: false,
                showContent: "auto",
            });
            slimSelectInstances.push(instance); // Сохраняем экземпляр в массив
        });
    }

    function clearSelects() {
        slimSelectInstances.forEach(instance => {
            if (instance.config.isMultiple) {
                instance.set([]);
            } else {
                instance.set('');
            }
        })
    }

    initSubmitForms();

    let reviewProviders = document
        .querySelectorAll(".reviews-select__item")
        .forEach((value) => {
            value.addEventListener("click", (event) => {
                let page = 1;
                if (value.classList.contains("is-active"))
                    value.classList.remove("is-active");
                else value.classList.add("is-active");

                let activeProviders = activeReviewsProviders();
                let container = document.getElementById("reviews_list");
                container.innerHTML = "";
                loadReviews(activeProviders.join(","), page);
            });
        });

    function activeReviewsProviders() {
        let providers = [];
        let actives = document
            .querySelectorAll(".reviews-select__item.is-active")
            .forEach((item1) => {
                if (item1.dataset.provider != undefined)
                    providers.push(item1.dataset.provider);
            });

        return providers;
    }

    function loadReviews(providers, page) {
        if (page == undefined) page = 1;
        if (providers == undefined) providers = "";
        let url = "";
        if (!isSubdomain) {
            url = "/" + citySlug;
        }
        url += "/reviews/search?page=" + page + "&providers=" + providers;
        let container = document.getElementById("reviews_list");
        let loading = document.querySelector(".loading");
        if (loading != undefined) loading.style.display = "block";
        fetch(url)
            .then(function (response) {
                return response.json();
            })
            .then(function (response) {
                if (response.success == true) {
                    container.innerHTML += response.reviews;
                    initRating();
                }
                loading.style.display = "none";
                if (response.count) {
                    document.querySelector(".reviews-total").innerHTML =
                        response.count;
                } else {
                    container.innerHTML =
                        "<div class='page-content__alert none'>Отзывов пока нет, станьте первым, оцените своего провайдера!</div>";
                }
                let load_more = document.querySelector(".load-more");
                if (load_more) {
                    if (response.hasMore) load_more.style.display = "block";
                    else load_more.style.display = "none";
                    load_more.dataset.page = page;
                    load_more
                        .querySelector("button")
                        .addEventListener("click", function (e) {
                            e.preventDefault();
                            loadReviews(
                                providers,
                                load_more.dataset.page * 1 + 1
                            );
                        });
                }
            })
            .catch(function (error) {
                container.innerHTML =
                    "<div class='page-content__alert none'>Отзывы не найдены</div>";
            });
    }

    let resetReviews = document.querySelector(".reviews-select__reset");
    if (resetReviews != undefined) {
        loadReviews("", 1);
        resetReviews.addEventListener("click", function () {
            document
                .querySelectorAll(".reviews-select__item.is-active")
                .forEach((item1) => {
                    item1.classList.remove("is-active");
                });
            let container = document.getElementById("reviews_list");
            container.innerHTML = "";
            loadReviews("", 1);
        });
    }

    // nav providers
    if (document.querySelector(".providers-nav")) {
        let menuSection = document.querySelectorAll(".providers-nav a");
        menuSection.forEach((v) => {
            v.onclick = (e) => {
                e.preventDefault();
                menuSection.forEach((j) => j.classList.remove("active"));
                v.classList.add("active");
            };
        });

        window.onscroll = () => {
            let mainSection = document.querySelectorAll(".provider-tariffs");
            let f = document.querySelector(".providers-nav");
            let secScr = document.querySelector(".blocks");

            var st = document.documentElement.scrollTop;
            if (st > secScr.getBoundingClientRect().y) {
                f.classList.add("visible");
            } else {
                f.classList.remove("visible");
            }

            mainSection.forEach((v, i) => {
                let rect = v.getBoundingClientRect().y;
                if (rect < window.innerHeight / 10 - 40) {
                    menuSection.forEach((v) => v.classList.remove("active"));
                    menuSection[i].classList.add("active");
                }
            });
        };
    }

    // Steps Modal Form Search address

    // if (
    //   window.location.hash &&
    //   document.querySelector("#modal-success-address")
    // ) {
    //   var hash = window.location.hash.substring(1);
    //   if (hash == "show_modal") {
    //     MicroModal.show("modal-success-address");
    //     bodyFixPosition();
    //     // history.pushState("", document.title, window.location.pathname);
    //   }
    // }
    function isYandexReferrer() {
        const yandexDomains = [
            'yandex', 'ya.ru'
        ];
        return yandexDomains.some(domain => document.referrer.includes(domain));
    }

    function isGoogleReferrer() {
        return document.referrer.includes('google');
    }

    if (
        document.referrer &&
        document.querySelector("#modal-success-address") &&
        window.performance &&
        performance.navigation.type !== 1
    ) {
        if (isGoogleReferrer()) {
            setTimeout(function() {
                MicroModal.show("modal-success-address");
                bodyFixPosition();
            }, 2000);
        } else {
            MicroModal.show("modal-success-address");
            bodyFixPosition();
        }
    }

    if (
        document.querySelector(".internet-po-adresu") &&
        document.referrer &&
        document.referrer.includes("internet-po-adresu") &&
        window.performance &&
        performance.navigation.type !== 1
    ) {
        const yOffset = -50;
        const element = document.querySelector(".internet-po-adresu");
        const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({top: y, behavior: "smooth"});
    }

    const modal_home = document.querySelector("#modal-success-address");
    let modal_home_close = document.querySelectorAll(".close-success-address");
    if (modal_home_close) {
        [].forEach.call(modal_home_close, function (item) {
            item.addEventListener("click", function () {
                bodyUnfixPosition();
                MicroModal.close("modal-success-address");
            });
        });
    }

    if (modal_home) {
        // const search_form_open = document.querySelector(".js-open-modal-form");
        // search_form_open.addEventListener("click", function () {
        //   modal_home.querySelector(".addres-step-1").classList.add("hide");
        //   modal_home.querySelector(".addres-step-2").classList.remove("hide");
        // });

        const form_home = modal_home.querySelector(
            "form.js-form_address_success"
        );
        const form_home_button = form_home.querySelector(".js-send-button");
        let form_home_button_t = "";
        if (form_home_button) {
            form_home_button_t = form_home_button.innerHTML;
        }
        let form_home_phone = modal_home.querySelector(".check-mobilephone");
        form_home_phone.addEventListener("input", () => {
            form_home.querySelector(".placeholder-error").classList.add("hide");
        });

        form_home.addEventListener("submit", (event) => {
            event.preventDefault();
            form_home_button.innerHTML = "Отправка...";
            let request = {
                name:
                    form_home.querySelector("[name=name]").value ||
                    "Заявка без имени",
                customer_text:
                    "Адрес:" + form_home.querySelector("[name=street]").value,
                phone: form_home.querySelector("[name=phone]").value,
            };

            if (!/^\+7 \(9\d{2}\) \d{3}-\d{2}-\d{2}$/.test(request.phone)) {
                modal_home
                    .querySelector(".placeholder-error")
                    .classList.remove("hide");
                modal_home
                    .querySelector(".js-form-cancel")
                    .classList.remove("hide");
                form_home_button.innerHTML = form_home_button_t;
                return false;
            }

            postData(form_home.getAttribute("action"), request)
                .then((data) => {
                    modal_home
                        .querySelector(".addres-step-1")
                        .classList.add("hide");
                    modal_home
                        .querySelector(".addres-step-2")
                        .classList.remove("hide");
                    form_home_button.innerHTML = form_home_button_t;
                })
                .catch(function (error) {
                    console.log(
                        "Не удалось отправить заявку, попробуйте позже."
                    );
                    form_home_button.innerHTML = form_home_button_t;
                });
        });
    }



    // form office
    const form_office = document.querySelector(".js-form-office");
    if (form_office) {
        const form_office_result = form_office.querySelector(".form-office__result");
        const form_office_button = form_office.querySelector(".js-send-button");
        let form_office_button_t = "";
        if (form_office_button) {
            form_office_button_t = form_office_button.innerHTML;
        }
        const phoneInput = form_office.querySelector("[name=phone]");
        setRussianErrorMessage(phoneInput);
        form_office.addEventListener("submit", (event) => {
            event.preventDefault();
            form_office_button.innerHTML = "Отправка...";
            let request = {
                name: form_office.querySelector("[name=name]").value,
                phone: form_office.querySelector("[name=phone]").value,
                customer_text:
                    "Комментарий: " +
                    form_office.querySelector("[name=office_text]").value +
                    "\n",
            };

            forEach(
                form_office.querySelectorAll("input:checked"),
                function (i, item) {
                    request.customer_text +=
                        item.name + ": " + item.value + "\n";
                }
            );

            forEach(
                form_office.querySelectorAll("option:checked"),
                function (i, item) {
                    request.customer_text +=
                        item.closest("select").name + ": " + item.value + "\n";
                }
            );

            postData(form_office.getAttribute("action"), request)
                .then((data) => {
                    if (data.errors) {
                        form_office.querySelectorAll('.error-message').forEach(el => el.remove());
                        form_office.querySelectorAll('input').forEach((input) => {
                            const errors = data.errors[input.name];
                            if (errors) {
                                const errorMessages = data.errors[input.name];
                                let label = document.getElementById(input.id + '-errors');

                                let errorText = errorMessages.join('<br/>');
                                label.innerHTML = `<p class="error-message error">` +
                                    errorText +
                                    `</p>`;
                            }
                        })
                        form_office_button.innerHTML = 'Отправить заявку'
                    } else {
                        form_office.querySelectorAll('.error-message').forEach(el => el.remove());
                        form_office_button.innerHTML = form_office_button_t;
                        form_office.querySelectorAll('input').forEach(input => {
                            input.value = '';
                        })
                        form_office.querySelector('textarea').value = '';
                        clearSelects();
                        form_office_result.classList.add('active');
                        form_office_result.querySelector(".success-message").style = 'display: block';
                        setTimeout(() => {
                            form_office_result.classList.remove('active');
                            form_office_result.querySelector(".success-message").style = 'display: none';
                        }, 3000);
                    }
                })
                .catch(function (error) {
                    console.log(error)
                    form_office_button.innerHTML = form_office_button_t;
                    form_office_result.classList.add('active');
                    form_office_result.querySelector(".error-message").style = 'display: block';
                    setTimeout(() => {
                        form_office_result.classList.remove('active');
                        form_office_result.querySelector(".error-message").style = 'display: none';
                    }, 3000);
                });
        });
    }

    // fixed header
    let elSelector = ".fixed-header";
    let element = document.querySelector(elSelector);
    if (element) {
        var elHeight = 0,
            elTop = 0,
            dHeight = 0,
            wHeight = 0,
            wScrollCurrent = 0,
            wScrollBefore = 0,
            wScrollDiff = 0;
        let header_drop_active = document.querySelectorAll(".header__drop");
        window.addEventListener("scroll", function () {
            elHeight = element.offsetHeight;
            dHeight = document.body.offsetHeight;
            wHeight = window.innerHeight;
            wScrollCurrent = window.pageYOffset;
            wScrollDiff = wScrollBefore - wScrollCurrent;
            elTop =
                parseInt(window.getComputedStyle(element).getPropertyValue("top")) +
                wScrollDiff;
            if (wScrollCurrent > 250) {
                element.classList.add("show_menu");
            } else {
                element.classList.remove("show_menu");
                header_drop_active.forEach((item) => {
                    item.classList.remove("is-active");
                });
            }

            if (wScrollCurrent <= 0) element.style.top = "0px";
            else if (wScrollDiff > 0)
                element.style.top = (elTop > 0 ? 0 : elTop) + "px";
            else if (wScrollDiff < 0) {
                if (wScrollCurrent + wHeight >= dHeight - elHeight)
                    element.style.top =
                        ((elTop = wScrollCurrent + wHeight - dHeight) < 0
                            ? elTop
                            : 0) + "px";
                else
                    element.style.top =
                        (Math.abs(elTop) > elHeight ? -elHeight : elTop) + "px";
            }

            wScrollBefore = wScrollCurrent;
        });
    }

    const modal_provider_name = document.querySelector(
        "[name=provider_name_modal]"
    );
    const filter_provider_name_select = document.querySelector(
        "select[name=provider_name]"
    );
    let filter_provider_name;
    if (filter_provider_name_select) {
        filter_provider_name = new SlimSelect({
            select: document.querySelector("select[name=provider_name]"),
            showSearch: false,
            showContent: "auto",
        });
    }
    if (modal_provider_name && filter_provider_name_select) {
        modal_provider_name.addEventListener("change", () => {
            filter_provider_name.set(modal_provider_name.value);
        });
    }

    const modal_provider_type = document.querySelector(
        "[name=provider_type_modal]"
    );
    const filter_provider_type_select = document.querySelector(
        "select[name=provider_type]"
    );
    let filter_provider_type;
    if (filter_provider_type_select) {
        filter_provider_type = new SlimSelect({
            select: filter_provider_type_select,
            showSearch: false,
            showContent: "auto",
        });
    }
    if (modal_provider_type && filter_provider_type_select) {
        modal_provider_type.addEventListener("change", () => {
            filter_provider_type.set(modal_provider_type.value);
        });
    }

    const modal_provider_sort = document.querySelector(
        "[name=provider_sort_modal]"
    );
    const filter_provider_sort_select = document.querySelector(
        "select[name=provider_sort]"
    );
    let filter_provider_sort;
    if (filter_provider_sort_select) {
        filter_provider_sort = new SlimSelect({
            select: filter_provider_sort_select,
            showSearch: false,
            showContent: "auto",
        });
    }
    if (modal_provider_sort && filter_provider_sort_select) {
        modal_provider_sort.addEventListener("change", () => {
            filter_provider_sort.set(modal_provider_sort.value);
        });
    }

    // search on page address

    const internetAddress = document.querySelector(".internet-po-adresu");
    if (internetAddress) {
        const internetAddress_clear = internetAddress.querySelector(
            ".page-search-address__close"
        );
        const internetAddressInput = internetAddress.querySelector(
            "[name=page-search-address-input]"
        );
        const internetAddress_list = internetAddress.querySelector(".column");
        const internetAddress_help = internetAddress.querySelector(
            ".page-search-address__help"
        );
        internetAddressInput.addEventListener("input", function (event) {
            let input = event.target;
            let query = reg_keyboard(event.target.value);
            let list = internetAddress_list.querySelectorAll(".item");
            let data = internetAddress_list.querySelectorAll(".item a");
            let clear_js = (ev1) => {
                ev1.closest(".item").style.display = "grid";
                ev1.closest(".item").setAttribute("data-selects", "");
                ev1.parentNode.setAttribute("data-select", "");
                // if (ev1.getAttribute("data-select"))
                //   ev1.parentNode.style.display = "grid";
                // else ev1.parentNode.style.display = "none";
            };
            let add_js = (ev1) => {
                ev1.parentNode.style.display = "none";
                ev1.parentNode.removeAttribute("data-select");
                if (
                    !ev1
                        .closest(".item")
                        .contains(
                            ev1.closest(".item").querySelector("[data-select]")
                        )
                ) {
                    ev1.closest(".item").style.display = "none";
                    ev1.closest(".item").removeAttribute("data-selects");
                }
            };
            if (input.value.length > 2) {
                internetAddress_clear.style.display = "flex";
                for (let i = 0, len = data.length; i < len; i++) {
                    data[i].innerHTML
                        .toLowerCase()
                        .includes(query.toLowerCase())
                        ? clear_js(data[i])
                        : add_js(data[i]);
                }
                for (let i = 0, lens = list.length; i < lens; i++) {
                    if (
                        list[i].parentNode.querySelectorAll("[data-selects]")
                            .length == 0
                    ) {
                        internetAddress_list.style.display = "none";
                        internetAddress_help.style.display = "block";
                    } else {
                        internetAddress_list.style.display = "block";
                        internetAddress_help.style.display = "none";
                    }
                }
            } else {
                internetAddress_clear.style.display = "none";
                for (let i = 0, len = data.length; i < len; i++) {
                    data[i].parentNode.removeAttribute("style");
                    data[i].parentNode.removeAttribute("data-select");
                    data[i].closest(".item").removeAttribute("data-selects");
                    data[i].closest(".item").removeAttribute("style");
                    internetAddress_list.style.display = "block";
                    internetAddress_help.style.display = "none";
                }
            }
            f_check_height_block();
        });
        internetAddress_clear.onclick = function () {
            internetAddressInput.value = "";
            internetAddressInput.focus();
            sendOnChange(internetAddressInput);
            f_check_height_block();
        };
    }

    const show_more_address = document.querySelector(".show-more-address-js");
    const f_check_height_block = () => {
        const button = document.querySelector(".show-more-address-js");
        const block = document.querySelector(".check-height-address");
        const height_block = block.offsetHeight;
        if (height_block > 500) {
            block.classList.add("over-size");
            button.closest(".show-more-address").classList.remove("hide");
        } else {
            block.classList.remove("over-size");
            button.closest(".show-more-address").classList.add("hide");
        }
    };
    if (show_more_address) {
        f_check_height_block();
        show_more_address.onclick = function () {
            document.querySelector(".over-size").classList.remove("over-size");
            show_more_address
                .closest(".show-more-address")
                .classList.add("hide");
        };
    }

    // providers ajax
    let providers = document.querySelector("#providers_list");
    if (providers) {
        let provider_name = document.querySelector("[name=provider_name]");
        let provider_type = document.querySelector("[name=provider_type]");
        let provider_sort = document.querySelector("[name=provider_sort]");
        let provider_limit = providers.children.length;
        let provider_load_more = document.querySelector(".load-more");
        provider_name.addEventListener("change", function (e) {
            providersLoad(
                this.value,
                provider_type.value,
                provider_sort.value,
                providers
            );
        });
        provider_type.addEventListener("change", function (e) {
            providersLoad(
                provider_name.value,
                this.value,
                provider_sort.value,
                providers
            );
        });
        provider_sort.addEventListener("change", function (e) {
            providersLoad(
                provider_name.value,
                provider_type.value,
                this.value,
                providers
            );
        });

        providersLoad(
            provider_name.value,
            provider_type.value,
            provider_sort.value,
            providers,
            mobile ? 6 : 12
        );

        if (provider_load_more) {
            provider_load_more
                .querySelector("button")
                .addEventListener("click", function (e) {
                    e.preventDefault();
                    providersLoad(
                        provider_name.value,
                        provider_type.value,
                        provider_sort.value,
                        providers,
                        providers.children.length + 6
                    );
                });
        }

        function providersLoad(name, type, sort, pane, limit, from) {
            from = from ? from : 0;
            let url = "";
            if (!isSubdomain) {
                url = "/" + citySlug;
            }
            url +=
                "/providers-list/?provider_name=" +
                name +
                "&type=" +
                type +
                "&sort=" +
                sort +
                "&limit=" +
                limit +
                "&from=" +
                from;
            let loading = document.querySelector("#loading-img");
            if (loading != undefined) loading.style.display = "none";
            fetch(url)
                .then(function (response) {
                    return response.json();
                })
                .then(function (response) {
                    if (response.providers && response.success == true) {
                        pane.innerHTML = response.providers;

                        if (provider_load_more) {
                            if (response.has_more)
                                provider_load_more.style.display = "block";
                            else provider_load_more.style.display = "none";
                        }

                        initModal();
                        initTariffModal();
                        initRating();
                        initToggle();
                    } else {
                        if (type != "") {
                            pane.innerHTML =
                                '<div class="page-content__alert none">Провайдеры не найдены. <br>Попробуйте изменить параметры поиска.</div>';
                        }
                    }
                });
        }
    }

    // tariffs ajax
    let tariffs = document.querySelector("#tariffs_list");
    let reset_filter_tariffs = document.querySelector(".block-filter__reset");
    if (tariffs) {
        let providers_list = [];
        let type_list = [];
        let tariffs_count = document.querySelector(".tariffs_count");
        let tariffs_name = document.querySelectorAll("[name=provider_name]");
        let tariffs_type = document.querySelectorAll("[name=provider_type]");
        let tariffs_sort = document.querySelectorAll("[name=provider_sort]");
        let result_panel = document.getElementById('result-panel');
        let type_sort = result_panel.dataset.defaultSort;
        let tariffs_load_more = document.querySelector(".load-more");
        let tariffsCount = Number(tariffs.dataset.tariffsCount);

        function getHiddenTariffs() {
            return document.querySelector("#tariffs_list").querySelectorAll('.hide');
        }

        function getTariffs() {
            return document.querySelector("#tariffs_list").querySelectorAll('.tariffs');
        }

        function showMorePreview() {
            const hiddenTariffs = getHiddenTariffs();
            Array.from(hiddenTariffs).slice(0, 10).forEach(card => {
                card.classList.remove('hide');
            });
        }

        tariffs_name.forEach(function (element) {
            element.addEventListener("change", function () {
                if (!providers_list.includes(element.value)) {
                    providers_list.push(element.value);
                } else {
                    providers_list = providers_list.filter(function (item) {
                        return item !== element.value;
                    });
                }
                tariffsLoad();
            });
        });
        tariffs_type.forEach(function (element) {
            element.addEventListener("change", function () {
                if (!type_list.includes(element.value)) {
                    type_list.push(element.value);
                } else {
                    type_list = type_list.filter(function (item) {
                        return item !== element.value;
                    });
                }
                tariffsLoad();
            });
        });

        tariffs_sort.forEach(function (element) {
            element.addEventListener("change", function () {
                type_sort = element.value;
                tariffsLoad();
            });
        });

        if (tariffs_load_more) {
            if (getTariffs().length === tariffsCount && getHiddenTariffs().length === 0) {
                tariffs_load_more.classList.add('hide');
            }
            tariffs_load_more
                .querySelector("button")
                .addEventListener("click", function (e) {

                    if (getHiddenTariffs().length !== 0) {
                        showMorePreview();
                        if (getTariffs().length === tariffsCount && getHiddenTariffs().length === 0) {
                            tariffs_load_more.classList.add('hide');
                        }
                    } else {
                        e.preventDefault();
                        tariffsLoad(10, getTariffs().length);
                    }

                });
        }

        if (reset_filter_tariffs) {
            reset_filter_tariffs.onclick = function (evt) {
                evt.preventDefault();
                clearFilterTarrifs();
                tariffsLoad();
            };
        }

        function clearFilterTarrifs() {
            let uncheck = document.getElementsByTagName("input");
            for (var i = 0; i < uncheck.length; i++) {
                if (uncheck[i].type == "checkbox") {
                    uncheck[i].checked = false;
                }
            }
            providers_list = [];
            type_list = [];
        }

        function tariffsLoad(limit, from) {
            limit = limit ? limit : 10;
            from = from ? from : 0;

            let container = document.querySelector("#tariffs_list");
            let url = "";
            if (!isSubdomain) {
                url = "/" + citySlug;
            }

            if (document.querySelector("input[name=provider_name_signle]")) {
                providers_list = document.querySelector(
                    "input[name=provider_name_signle]"
                ).value;
            }

            url += `/tariffs-list/?provider_name=${providers_list}&type=${type_list}&sort=${type_sort}&limit=${limit}&from=${from}`;
            if (document.querySelector("input[name=search]")) {
                url += "&" + document.querySelector("input[name=search]").value;
            }
            if (document.querySelector("input[name=sort_promo]")) {
                url += "&promo=1";
            }
            if (document.querySelector("input[name=conditions]")) {
                url +=
                    "&conditions=" +
                    document.querySelector("input[name=conditions]").value;
            }
            let loading = document.querySelector("#loading-img");
            if (loading != undefined) loading.style.display = "none";
            fetch(url)
                .then(function (response) {
                    return response.json();
                })
                .then(function (response) {
                    if (response.tariffs && response.success == true) {
                        if (from === 0) {
                            container.innerHTML = response.tariffs;
                        } else {
                            container.insertAdjacentHTML('beforeend', response.tariffs);
                        }
                        if (tariffs_load_more) {
                            if (response.has_more)
                                tariffs_load_more.style.display = "block";
                            else tariffs_load_more.style.display = "none";
                        }

                        if (response.count !== 0) {
                            tariffs_count.innerHTML = `${declOfNum(response.count, [
                                "Найден",
                                "Найдено",
                                "Найдено",
                            ])} ${response.count} ${declOfNum(response.count, [
                                "тариф",
                                "тарифа",
                                "тарифов",
                            ])} от ${response.providers_count} ${declOfNum(
                                response.providers_count,
                                ["провайдера", "провайдеров", "провайдеров"]
                            )} `;
                        }
                        initTippyJS();
                        initModal();
                        initTariffModal();
                    } else {
                        tariffs_count.innerHTML = `Найдено 0 тарифов`;
                        container.innerHTML =
                            '<div class="page-content__alert none">Тарифы не найдены. <br>Попробуйте изменить параметры поиска.</div>';
                    }
                    tariffsCount = response.count ?? 0;

                    if (providers_list.length || type_list.length) {
                        reset_filter_tariffs.classList.remove("hide");
                    } else {
                        reset_filter_tariffs.classList.add("hide");
                    }
                });
        }
    }

    //Collapsible sidebar element
    var sidebarElements = document.getElementsByClassName(
        "sidebar__collapsible"
    );

    for (let i = 0; i < sidebarElements.length; i++) {
        sidebarElements[i].addEventListener("click", function () {
            this.classList.toggle("active");
        });
    }

    //Load more articles button
    var loadMoreButtons = document.getElementsByClassName("load-more-btn");
    let loadMoreFunction = async function () {
        let response = await fetch(this.dataset.url, {
            headers: {
                "X-Requested-With": "XMLHttpRequest",
            },
        });

        if (response.ok) {
            let html = await response.text();
            let list = this.parentElement.parentElement;
            let loadMoreBlocks = document.getElementsByClassName("load-more");
            while (loadMoreBlocks[0]) {
                loadMoreBlocks[0].parentNode.removeChild(loadMoreBlocks[0]);
            }

            list.innerHTML += html;
            loadMoreBlocks = document.getElementsByClassName("load-more-btn");
            if (loadMoreBlocks[0]) {
                loadMoreBlocks[0].addEventListener("click", loadMoreFunction);
            }
        } else {
            console.log("Ошибка HTTP: " + response.status);
        }
    };

    for (let i = 0; i < loadMoreButtons.length; i++) {
        loadMoreButtons[i].addEventListener("click", loadMoreFunction);
    }

    // send review form

    initJsModalSubmitReview();
    initCheckNote();

    // check mobile city confirm
    function setCookie(name, value, days) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }

        // Получаем домен с текущего поддомена
        const domain = window.location.hostname.split('.').slice(-2).join('.');

        document.cookie = `${name}=${value}${expires}; path=/; domain=${domain}`;
    }

    function getCookie(name) {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        return match ? match[2] : null;
    }

    // проверка куки
    if (getCookie("check_city") === "none") {
        document.querySelectorAll(".header__mobile-check-city ").forEach(el => el.classList.add("hidden"));
        document.querySelectorAll(".header__mobile-city ").forEach(el => el.classList.remove("hidden"));
    } else {
        document.querySelectorAll(".header__mobile-check-city ").forEach(el => el.classList.remove("hidden"));
        document.querySelectorAll(".header__mobile-city ").forEach(el => el.classList.add("hidden"));
    }

    // установка куки при подтверждении города
    document.querySelectorAll(".confirm-city").forEach(button => {
        button.addEventListener("click", function (event) {
            event.preventDefault();
            setCookie("check_city", "none", 7);
            document.querySelectorAll(".header__mobile-check-city ").forEach(el => el.classList.add("hidden"));
            document.querySelectorAll(".header__mobile-city ").forEach(el => el.classList.remove("hidden"));
        });
    });


    const map = document.querySelector('.map-address');
    const openMapButton = document.querySelector('.hideMap');

    if (map && openMapButton) {
        openMapButton.addEventListener('click', () => {
            const buttonText = openMapButton.querySelector('p');

            map.classList.toggle('active');
            openMapButton.classList.toggle('active');

            if (openMapButton.classList.contains('active')) {
                buttonText.textContent = 'Показать карту'
            } else {
                buttonText.textContent = 'Скрыть карту'
            }
        });
    }
})
